import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';
import { RotateCcw, Plus, Trash2, Package, Search, CheckCircle2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReturnsPage = () => (
  <Layout>
    <ReturnsContent />
  </Layout>
);

export const ReturnsContent = () => {
  const [returns, setReturns] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({ sale_number: '', customer_name: '', reason: '', refund_method: 'dinheiro', notes: '' });
  const [itemForm, setItemForm] = useState({ product_id: '', product_name: '', quantity: '', unit_price: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Nova: busca de venda por número (Auto-fetch)
  const [saleLookup, setSaleLookup] = useState({ status: 'idle', sale: null, error: '' }); // idle | loading | found | not_found | error
  const [showManual, setShowManual] = useState(false); // toggle para adição manual (fallback)
  const lookupTimer = useRef(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [rRes, pRes] = await Promise.all([
        axios.get(`${API}/returns`, { withCredentials: true }),
        axios.get(`${API}/products`, { withCredentials: true })
      ]);
      setReturns(rRes.data);
      setProducts(pRes.data);
    } catch (err) { console.error(err); }
  };

  // Busca a venda quando o número é digitado (debounce de 400ms)
  const fetchSale = async (numStr) => {
    const num = parseInt(numStr, 10);
    if (!num || num <= 0) {
      setSaleLookup({ status: 'idle', sale: null, error: '' });
      return;
    }
    setSaleLookup({ status: 'loading', sale: null, error: '' });
    try {
      const r = await axios.get(`${API}/sales/by-number/${num}`, { withCredentials: true });
      const sale = r.data;
      setSaleLookup({ status: 'found', sale, error: '' });
      // Auto-preencher cliente se a venda tiver
      if (sale.customer_name && !formData.customer_name) {
        setFormData((f) => ({ ...f, customer_name: sale.customer_name }));
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setSaleLookup({ status: 'not_found', sale: null, error: `Venda #${num} não encontrada.` });
      } else {
        setSaleLookup({ status: 'error', sale: null, error: 'Erro ao buscar venda.' });
      }
    }
  };

  const handleSaleNumberChange = (val) => {
    setFormData({ ...formData, sale_number: val });
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(() => fetchSale(val), 400);
  };

  // Devolver 1 item da venda: adiciona (ou incrementa) na lista de devolução
  // usando o MESMO preço da venda (evita divergência de valor).
  const returnSaleItem = (saleItem, requestedQty = null) => {
    const qty = requestedQty != null ? parseFloat(requestedQty) : (saleItem.quantity || 1);
    if (!qty || qty <= 0) { setError('Informe uma quantidade válida'); return; }
    // Checar se já existe na lista de devolução (mesmo product_id + mesmo preço)
    const idx = items.findIndex((it) => it.product_id === saleItem.product_id && Math.abs(it.unit_price - saleItem.unit_price) < 0.001);
    // Máximo que pode ser devolvido é a qty vendida (impede devolver mais do que foi vendido)
    const alreadyIn = idx >= 0 ? items[idx].quantity : 0;
    const maxAllowed = saleItem.quantity - alreadyIn;
    if (maxAllowed <= 0) {
      setError(`Este item já foi totalmente adicionado (${saleItem.quantity} un). Remova a linha antes.`);
      return;
    }
    const finalQty = Math.min(qty, maxAllowed);
    const price = parseFloat(saleItem.unit_price);
    if (idx >= 0) {
      const next = [...items];
      const newQty = next[idx].quantity + finalQty;
      next[idx] = { ...next[idx], quantity: newQty, subtotal: newQty * price };
      setItems(next);
    } else {
      setItems([...items, {
        product_id: saleItem.product_id,
        product_name: saleItem.product_name,
        quantity: finalQty,
        unit_price: price,
        subtotal: finalQty * price,
      }]);
    }
    setError('');
  };

  const addItem = () => {
    if (!itemForm.product_id || !itemForm.quantity || !itemForm.unit_price) {
      setError('Preencha produto, quantidade e preço');
      return;
    }
    const p = products.find(x => x.id === itemForm.product_id);
    const qty = parseFloat(itemForm.quantity);
    const price = parseFloat(itemForm.unit_price);
    setItems([...items, {
      product_id: itemForm.product_id,
      product_name: p?.name || itemForm.product_name,
      quantity: qty,
      unit_price: price,
      subtotal: qty * price
    }]);
    setItemForm({ product_id: '', product_name: '', quantity: '', unit_price: '' });
    setError('');
  };

  const total = items.reduce((s, i) => s + i.subtotal, 0);

  const resetForm = () => {
    setShowForm(false);
    setItems([]);
    setError('');
    setSaleLookup({ status: 'idle', sale: null, error: '' });
    setShowManual(false);
    setFormData({ sale_number: '', customer_name: '', reason: '', refund_method: 'dinheiro', notes: '' });
  };

  const extractError = (err) => {
    // FastAPI 422 returns detail as array; 400/404/500 returns string.
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (Array.isArray(detail)) {
      return detail
        .map((d) => {
          const field = Array.isArray(d.loc) ? d.loc.filter((x) => x !== 'body').join('.') : '';
          return field ? `${field}: ${d.msg}` : d.msg;
        })
        .join(' | ');
    }
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (err?.message) return `${err.message}${status ? ` (HTTP ${status})` : ''}`;
    return 'Erro desconhecido ao salvar devolução';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (items.length === 0) { setError('Adicione ao menos um item'); return; }
    if (!formData.reason || formData.reason.length < 3) { setError('Informe o motivo da devolução (mínimo 3 caracteres)'); return; }
    // Sanity check: items com product_id ausente
    const invalid = items.find((it) => !it.product_id);
    if (invalid) { setError('Existe um item sem produto selecionado. Remova-o e re-adicione.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/returns`, {
        sale_number: formData.sale_number ? parseInt(formData.sale_number) : null,
        customer_name: formData.customer_name || null,
        items,
        total: parseFloat(total.toFixed(2)),
        reason: formData.reason,
        refund_method: formData.refund_method,
        notes: formData.notes
      }, { withCredentials: true });
      load();
      setItems([]);
      setFormData({ sale_number: '', customer_name: '', reason: '', refund_method: 'dinheiro', notes: '' });
      setSaleLookup({ status: 'idle', sale: null, error: '' });
      setShowManual(false);
      setShowForm(false);
    } catch (err) {
      console.error('Return submit error:', err?.response || err);
      setError(extractError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="p-8" data-testid="returns-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-black text-slate-900 tracking-tight">Devoluções</h2>
        <Button onClick={() => setShowForm(!showForm)} className="bg-red-600 hover:bg-red-700" data-testid="add-return-btn">
          <RotateCcw size={20} className="mr-2" /> Nova Devolução
        </Button>
      </div>

        {showForm && (
          <div className="bg-white rounded-md border border-slate-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Registrar Devolução</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Nº da Venda</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={formData.sale_number}
                      onChange={(e) => handleSaleNumberChange(e.target.value)}
                      placeholder="Ex: 42 (busca automática)"
                      data-testid="return-sale-number"
                      className={
                        saleLookup.status === 'found' ? 'border-emerald-500 pr-9' :
                        saleLookup.status === 'not_found' || saleLookup.status === 'error' ? 'border-red-500 pr-9' :
                        'pr-9'
                      }
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      {saleLookup.status === 'loading' && <Search size={16} className="text-slate-400 animate-pulse" />}
                      {saleLookup.status === 'found' && <CheckCircle2 size={18} className="text-emerald-600" data-testid="sale-found-icon" />}
                      {(saleLookup.status === 'not_found' || saleLookup.status === 'error') && <XCircle size={18} className="text-red-500" data-testid="sale-not-found-icon" />}
                    </div>
                  </div>
                  {saleLookup.status === 'not_found' && <p className="text-xs text-red-600">{saleLookup.error}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Cliente (opcional)</Label>
                  <Input value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Forma de Restituição *</Label>
                  <Select value={formData.refund_method} onValueChange={(v) => setFormData({ ...formData, refund_method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dinheiro">Dinheiro</SelectItem>
                      <SelectItem value="credito_loja">Crédito na Loja</SelectItem>
                      <SelectItem value="troca">Troca por Produto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Motivo *</Label>
                  <Input value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="Ex: Produto avariado, validade vencida..." required />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Observações</Label>
                  <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                </div>
              </div>

              {/* Painel de itens da venda encontrada — clique em "Devolver" para adicionar */}
              {saleLookup.status === 'found' && saleLookup.sale && (
                <div className="border border-emerald-200 bg-emerald-50/40 rounded-md p-4" data-testid="sale-items-panel">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-emerald-900 flex items-center gap-2">
                        <CheckCircle2 size={18} className="text-emerald-600" />
                        Venda #{saleLookup.sale.sale_number} encontrada
                      </h3>
                      <p className="text-xs text-slate-600 mt-0.5">
                        {saleLookup.sale.customer_name ? `Cliente: ${saleLookup.sale.customer_name} · ` : ''}
                        Total: <strong className="text-slate-900">R$ {parseFloat(saleLookup.sale.total || 0).toFixed(2)}</strong> ·
                        Data: {new Date(saleLookup.sale.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest font-bold bg-emerald-600 text-white px-2 py-1 rounded">
                      Clique em &quot;Devolver&quot;
                    </span>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs uppercase text-slate-500 border-b border-emerald-200">
                        <th className="px-2 py-2 text-left">Produto</th>
                        <th className="px-2 py-2 text-right">Qtd Vendida</th>
                        <th className="px-2 py-2 text-right">Preço Unit.</th>
                        <th className="px-2 py-2 text-right">Subtotal</th>
                        <th className="px-2 py-2 text-center w-40">Devolver</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(saleLookup.sale.items || []).map((it, i) => {
                        // Quantidade já adicionada à devolução para este item
                        const alreadyIn = items
                          .filter((x) => x.product_id === it.product_id && Math.abs(x.unit_price - it.unit_price) < 0.001)
                          .reduce((s, x) => s + x.quantity, 0);
                        const remaining = Math.max(0, (it.quantity || 0) - alreadyIn);
                        return (
                          <tr key={i} className="border-b border-emerald-100 hover:bg-white" data-testid={`sale-item-row-${i}`}>
                            <td className="px-2 py-2 font-medium text-slate-800">{it.product_name}</td>
                            <td className="px-2 py-2 text-right text-slate-700">{it.quantity}</td>
                            <td className="px-2 py-2 text-right font-mono text-slate-700">R$ {parseFloat(it.unit_price).toFixed(2)}</td>
                            <td className="px-2 py-2 text-right font-mono font-bold text-slate-900">R$ {parseFloat(it.subtotal || (it.quantity * it.unit_price)).toFixed(2)}</td>
                            <td className="px-2 py-2 text-center">
                              {remaining > 0 ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => returnSaleItem(it, remaining)}
                                  className="bg-red-600 hover:bg-red-700 text-white h-8 px-3"
                                  data-testid={`btn-return-item-${i}`}
                                >
                                  <RotateCcw size={13} className="mr-1" /> Devolver {remaining !== it.quantity ? `(${remaining})` : ''}
                                </Button>
                              ) : (
                                <span className="text-xs text-emerald-700 font-bold inline-flex items-center gap-1">
                                  <CheckCircle2 size={13} /> Adicionado
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Items */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-900">Itens da Devolução ({items.length})</h3>
                  <button
                    type="button"
                    onClick={() => setShowManual(!showManual)}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                    data-testid="toggle-manual-item"
                  >
                    {showManual ? '− Ocultar adição manual' : '+ Adicionar item avulso (sem venda vinculada)'}
                  </button>
                </div>
                {showManual && (
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-3 bg-slate-50 p-3 rounded border border-slate-200">
                  <div className="md:col-span-5 space-y-1">
                    <Label className="text-xs">Produto</Label>
                    <Select value={itemForm.product_id} onValueChange={(v) => {
                      const p = products.find(x => x.id === v);
                      setItemForm({ ...itemForm, product_id: v, product_name: p?.name || '', unit_price: p?.price?.toString() || '' });
                    }}>
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3 space-y-1">
                    <Label className="text-xs">Quantidade</Label>
                    <Input type="number" step="0.01" value={itemForm.quantity} onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })} />
                  </div>
                  <div className="md:col-span-3 space-y-1">
                    <Label className="text-xs">Preço Unit.</Label>
                    <Input type="number" step="0.01" value={itemForm.unit_price} onChange={(e) => setItemForm({ ...itemForm, unit_price: e.target.value })} />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    <Button type="button" onClick={addItem} className="bg-blue-600 hover:bg-blue-700 w-full">+</Button>
                  </div>
                  </div>
                )}

                {items.length > 0 && (
                  <table className="w-full border border-slate-200 rounded">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-bold uppercase">Produto</th>
                        <th className="px-3 py-2 text-right text-xs font-bold uppercase">Qtd</th>
                        <th className="px-3 py-2 text-right text-xs font-bold uppercase">Preço</th>
                        <th className="px-3 py-2 text-right text-xs font-bold uppercase">Subtotal</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, idx) => (
                        <tr key={idx} className="border-t border-slate-200">
                          <td className="px-3 py-2">{it.product_name}</td>
                          <td className="px-3 py-2 text-right">{it.quantity}</td>
                          <td className="px-3 py-2 text-right">R$ {it.unit_price.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right font-bold">R$ {it.subtotal.toFixed(2)}</td>
                          <td className="px-3 py-2"><button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-red-600"><Trash2 size={14} /></button></td>
                        </tr>
                      ))}
                      <tr className="bg-red-50 font-bold">
                        <td colSpan={3} className="px-3 py-2 text-right">TOTAL DA DEVOLUÇÃO</td>
                        <td className="px-3 py-2 text-right text-red-600">R$ {total.toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>

              {error && <Alert variant="destructive" data-testid="return-error-alert"><AlertDescription>{error}</AlertDescription></Alert>}
              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700">{loading ? 'Salvando...' : 'Registrar Devolução'}</Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Data</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Venda #</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Motivo</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Itens</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Restituição</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {returns.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Nenhuma devolução registrada</td></tr>}
              {returns.map(r => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm">{new Date(r.created_at).toLocaleString('pt-BR')}</td>
                  <td className="px-4 py-3 text-sm font-bold text-blue-700">{r.sale_number ? `#${r.sale_number}` : '-'}</td>
                  <td className="px-4 py-3 text-sm">{r.customer_name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{r.reason}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{r.items?.length || 0}</td>
                  <td className="px-4 py-3 text-sm capitalize">{r.refund_method.replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-right font-bold text-red-600">R$ {r.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
    </div>
  );
};
